import type { UserRole } from '@statura/types';
import { LEAVE_HOURS_SOURCE_LABELS } from '@statura/utils/leave-hours-resolution';

/**
 * Special-case display overrides for acronyms and legislative terms
 * that should NOT be generically title-cased.
 */
const ENUM_DISPLAY_OVERRIDES: Record<string, string> = {
  MANUAL_HANDLING: 'Manual handling',
  RATE_OPERATIONAL_ROUNDING_RECONCILIATION_REQUIRED:
    'Estimates await Services Australia rounding reconciliation',
  RESOLVED_OUTSIDE_STATURA: 'Resolved outside Statura',
  SERVICE_AREA_MISSING: 'Service area not set',
  // SAH E1 origin-bound delivery holds (S1 20991029700005).
  MANUAL_FINANCE_REVIEW_REQUIRED:
    "Finance review needed: this visit's attendance was recorded and billing is held until the brokered-visit finance review is available",
  SERIES_FINANCE_REVIEW_REQUIRED:
    "Finance review needed: this repeating visit's attendance was recorded and billing is held until the brokered-visit finance review is available",
  // W3a contractor delivery holds (contractor-management.md §5.3).
  ACTUALS_MISSING: 'Held until visit hours are confirmed',
  CONTRACTOR_PRICING_UNRESOLVED: 'Held until the service price is confirmed',
  CONTRACTOR_WORKER_UNLINKED: 'Waiting for worker link',
  DEGRADED: 'Status unknown',
  UNDETERMINED: 'Status unknown',
  AGED_CARE_SCREENING_UNDETERMINED: 'Aged care screening status unknown',
  INACTIVE: 'Inactive',
  before_first_shift: 'Before first shift',
  within_30_days: 'Within 30 days',
  collect_by: 'Collect by date',
  after_start: 'After start',
  current_workforce: 'Current workforce',
  former_worker: 'Former worker',
  LOG_AFTER: 'Record work after delivery',
  ROSTERED: 'Rostered delivery',
  RAC: 'Residential aged care (RAC)',
  TCP: 'Transition care (TCP)',
  STRC: 'Short-term restorative care',
  NOT_CURRENT: 'Not current',
  HR_OFFICER: 'HR officer',
  HR_DIRECTOR: 'HR Director',
  CLINICAL_SCOPE_MINIMUM: 'Minimum clinical scope',
  ENFORCE_ROSTER: 'Enforce roster',
  ENFORCE_ALL: 'Enforce roster and downstream controls',
  ENFORCE_CONTEXT: 'Enforce pay context',
  ENFORCE_EXPORT: 'Enforce pay export',
  UNSET_ENFORCE_DEFAULT: 'Legacy unset (effective Enforce)',
  BOOTSTRAP_EFFECTIVE_V1: 'Inherited compatibility settings',
  NEW_TENANT_DEFAULT_V2: 'New provider defaults',
  CONTROLLED_PATCH_V2: 'Explicit audited provider choice',
  RAC_RESIDENT_FEES: 'Residential aged care fees',
  CONTRIBUTION_CAPS: 'Contribution caps',
  MEANS_TEST: 'Means test',
  RESIDENTIAL_CHARGE: 'Residential charge',
  FEE_ADVICE_CHANGED: 'Fee advice changed after billing',

  PRODA_DEVICE: 'PRODA device activation',
  PRODA_KEY: 'PRODA signing key',
  NASH_CERTIFICATE: 'NASH certificate',
  B2G_SUBSCRIPTION: 'B2G subscription credential',
  B2G_REGISTRATION: 'B2G machine credential',
  OUTCOME_UNKNOWN: 'Outcome needs reconciliation',
  CHECK_FAILED: 'Check failed',
  DISALLOW: 'No negative balance',
  ALLOW_WITH_REASON: 'Negative balance requires a reason',
  ALLOW_WITH_REASON_AND_CHECKER: 'Negative balance requires a reason and second approver',
  // Approved-timesheet correction kinds and the approval-withdrawal events.
  // The propose form on Payroll > Corrections names these in plain language;
  // the history table, badges and evidence views must say the same words
  // (design authority, 8 Sep 2026, UX-13) instead of the title-case fallback
  // ("Source Restatement", "Reopened For Correction").
  SOURCE_RESTATEMENT: 'Corrected facts',
  VOID_DUPLICATE: 'Void duplicate',
  REPLACE_WITH_LEAVE: 'Replaced with leave',
  TIME_BREAK: 'Time or break correction',
  REOPENED_FOR_CORRECTION: 'Reopened for correction',
  TIMESHEET_APPROVAL_REOPENED: 'Approval withdrawn',
  // CHSP reporting and My Aged Care identity reconciliation.
  CHSP_REPORTING: 'CHSP reporting',
  CHSP_MAC_COMPLETENESS: 'CHSP My Aged Care ID completeness',
  DEX_MONTHLY: 'CHSP monthly DEX report',
  FINANCIAL_DECLARATION: 'CHSP annual financial declaration',
  CHILD_SAFETY: 'CHSP child safety compliance statement',
  MISSING_MAC_ID: 'My Aged Care ID missing',
  CONFLICTING_MAC_ID: 'My Aged Care IDs do not match',
  ADOPT_CLIENT_MAC: 'Use the client record My Aged Care ID in DEX',
  CORRECT_CLIENT_MAC: 'Correct the client record My Aged Care ID',
  SSD_ONLY: 'Sector Support and Development only',
  // Acronyms
  CHSP: 'CHSP',
  NDIS: 'NDIS',
  DVA: 'DVA',
  AHPRA: 'AHPRA',
  AN_ACC: 'AN-ACC',
  MAC_ID: 'MAC ID',
  SAH: 'SAH',
  EA: 'EA',
  EBA: 'EBA',
  STP: 'STP',
  MYOB: 'MYOB',
  KEYPAY: 'KeyPay',
  PCA: 'PCA',
  RN: 'RN',
  EN: 'EN',
  HCA1: 'HCA1',
  HCA2: 'HCA2',
  HCA3: 'HCA3',
  HCA4: 'HCA4',
  HCA5: 'HCA5',
  HCA6: 'HCA6',
  // Title Case (match generic formatEnum title-caser) so roster/care-plan
  // labels stay consistent with existing tests and UI copy.
  AWAITING_CLOCK: 'No clock yet',
  EVERYDAY_LIVING: 'Everyday Living',
  INDEPENDENCE: 'Independence',
  CLINICAL_CARE: 'Clinical Care',
  CARE_WORKER: 'Care Worker',
  PERSONAL_CARE: 'Personal Care',
  MEAL_PREPARATION: 'Meal Preparation',
  DOMESTIC_ASSISTANCE: 'Domestic Assistance',
  SOCIAL_SUPPORT: 'Social Support',
  CSV: 'CSV',
  XLSX: 'XLSX',
  INERVA: 'Inerva',
  SUPPORT_AT_HOME: 'Support at Home',
  NONE_SOURCE_ONLY: 'None (source only)',
  VACANCY: 'Vacancy',
  HOLD: 'Hold',
  AGENCY: 'Agency worker',
  AGENCY_UNFILLED: 'Agency unfilled',
  STAFFED: 'Staffed',
  NOT_A_SHIFT: 'Not a shift',
  ASSUMED_REPEAT: 'Assumed repeat',
  CONFIRMATION_PENDING: 'Confirmation pending',
  OVERLAP: 'Overlap',
  UNMATCHED_NAME: 'Unmatched name',
  // Without these the generic title-caser renders them as "Whs", "Ain", "Sirs"
  // and so on wherever the token reaches a label or a dropdown option.
  WHS: 'WHS',
  AIN: 'AIN',
  SIRS: 'SIRS',
  ACQSC: 'ACQSC',
  LMS: 'LMS',
  TFN: 'TFN',
  ABN: 'ABN',
  BPAY: 'BPAY',
  ELMO: 'ELMO',
  GST: 'GST',
  // Screening types
  AHPRA_REGISTRATION: 'AHPRA Registration',
  NATIONAL_POLICE_CHECK: 'National Police Check',
  NDIS_WORKER_SCREENING: 'NDIS Worker Screening',
  WORKING_WITH_CHILDREN: 'Working with Children',
  AGED_CARE_WORKER_SCREENING: 'Aged Care Worker Screening',
  INTERNATIONAL_POLICE_CHECK: 'International Police Check',
  FIRST_AID: 'First Aid',
  CPR: 'CPR',
  // Risk levels
  NOT_ASSESSED: 'Not Assessed',
  not_assessed: 'Not Assessed',
  // SIRS incident types — current categories reflect Aged Care Act 2024 s 16.
  UNREASONABLE_USE_OF_FORCE: 'Unreasonable use of force',
  UNLAWFUL_SEXUAL_CONTACT: 'Unlawful sexual contact',
  PSYCHOLOGICAL_EMOTIONAL_ABUSE: 'Psychological or emotional abuse',
  UNEXPECTED_DEATH: 'Unexpected death',
  STEALING_FINANCIAL_COERCION: 'Stealing or financial coercion',
  NEGLECT: 'Neglect',
  INAPPROPRIATE_RESTRICTIVE_PRACTICES: 'Inappropriate restrictive practices',
  UNEXPLAINED_ABSENCE: 'Unexplained absence',
  SERIOUS_HARM: 'Historical serious harm category',
  OTHER_SERIOUS: 'Historical other serious category',
  // QI domains
  physical_restraint: 'Restrictive practices',
  PHYSICAL_RESTRAINT: 'Restrictive practices',
  medication_management: 'Medication management',
  MEDICATION_MANAGEMENT: 'Medication management',
  // Funding programs — defensive acronym preservation
  HCP: 'HCP',
  hcp: 'HCP',
  // Leave settlement axis (lib/leave/leave-settlement-axis.ts). Tokens whose
  // generic title-casing would misrender a product/provider name or read as
  // jargon on a worker-facing chip.
  STATURA_PAID: 'Paid by Statura',
  SENT_TO_XERO: 'Sent to Xero',
  SETTLED_EXTERNAL: 'Settled externally',
  PUSH_INDETERMINATE: 'Push outcome unknown',
  RECONCILE_MISMATCH: 'Reconcile mismatch',
  VOID_PENDING: 'Payroll removal pending',
  VOID_FAILED: 'Payroll removal failed',
  REVERSAL_REQUIRED: 'Reversal required',
  REVERSAL_RECONCILED: 'Reversal reconciled',
  REVERSAL_FAILED: 'Reversal failed',
  // Leave push faults (lib/leave/leave-push-core.ts, WP-3 D1). Operator
  // language for the leave-payments row detail and exception card — the raw
  // token would read as data-model jargon.
  LEAVE_WORKER_PAY_HELD: 'All worker pay held for leave correction',
  LEAVE_PRICING_WORKER_HOLD: 'All worker pay held for leave correction',
  MAPPING_UNRESOLVED: 'Xero mapping missing or ambiguous',
  PAY_PERIODS_UNRESOLVED: 'Xero pay calendar unavailable',
  PAYLOAD_INVALID: 'Leave days do not build a valid Xero request',
  NO_SEGMENTS: 'No leave days recorded',
  TOTAL_HOURS_UNAVAILABLE: 'Request hours unavailable',
  REQUEST_NOT_APPROVED: 'Not approved',
  ALREADY_PUSHED: 'Already sent to Xero',
  SYNC_STATE_NOT_PUSHABLE: 'Not in a pushable state',
  INDETERMINATE_UNRESOLVED: 'Push outcome unknown — resolve before retrying',
  PROVIDER_REJECTED: 'Xero refused the request',
  PROVIDER_RATE_LIMITED: 'Xero is busy — try again shortly',
  MARKER_STAMP_FAILED: 'Sent to Xero but not recorded — resolve to adopt it',
  STATE_WRITE_FAILED: 'Could not record the outcome — try again',
  SELF_PUSH_FORBIDDEN: 'Your own request — another operator must send it',
  SHARED_ACCOUNT_FORBIDDEN: 'Shared account — sign in as yourself to move money',
  PUSH_IN_FLIGHT: 'Already being sent — wait for the outcome',
  NOT_INDETERMINATE: 'Nothing to resolve for this request',
  LEAVE_PUSH_LOADING_GATES_UNRESOLVED: 'Annual leave loading not ready to send',
  LEAVE_PUSH_LOADING_DISPATCH_WITHHELD: 'Annual leave loading delivery not switched on',
  // Attended Xero priced-payslip refusals. These can reach the journal and
  // must not degrade to implementation tokens in an operator-facing panel.
  PAYSLIP_CENTS_UNREPRESENTABLE: 'Approved pay cannot be represented at Xero wire precision',
  PAYSLIP_WRITE_TARGET_RATE_HAS_FOREIGN_LINE:
    'Targeted Xero earnings rate has a manual or non-template payment',
  RESOLVER_PROVIDER_UNAVAILABLE: 'Could not check Xero — try again',
  RESOLVER_SCOPE_INSUFFICIENT: 'Xero check could not cover these dates — needs manual review',
  RESOLVER_AMBIGUOUS: 'Conflicting records in Xero — needs manual review',
  RETRY_EXISTING_APPLICATION_FOUND:
    'This leave is already in Xero. Link it instead of sending again: open Leave payments on Roster > Time & closeout > Timesheets and choose Resolve now.',
  // WP-6 [CU3] push-time balance verdict recheck faults.
  BALANCE_INSUFFICIENT_AT_PUSH: 'Balance no longer covers this leave — cannot send',
  XERO_APPLICATION_ALREADY_PRESENT:
    'Xero already has leave of this type on these dates — check it was not keyed by hand before sending',
  PERIOD_ALREADY_POSTED: 'This pay period is already posted in Xero — not sent again',
  PERIOD_STATUS_UNVERIFIED:
    'Pay period status not confirmed from Xero yet — will retry after the next sync',
  ORIGINAL_IN_PROCESSED_PERIOD:
    'The earlier leave is in a processed Xero period — payroll must confirm the correction before this change is sent',
  BALANCE_CONTESTED_AT_PUSH:
    'Balance covers this leave, but pending request(s) contest it — decide the pending request or override',
  BALANCE_STALE_AT_PUSH: 'Balance too old to verify — refresh, or send with an override',
  BALANCE_UNVERIFIABLE_AT_PUSH: 'Balance could not be verified — refresh, or send with an override',
  BALANCE_AUTHORISATION_FAILED:
    'Your role cannot read the balance basis — authorisation parity fix required, not an override',
  BALANCE_OVERRIDE_REASON_REQUIRED: 'Override reason too short — at least 10 characters',
  // Leave decision axis (lib/leave/leave-operational-projection.ts). Without
  // this the generic title-caser renders the queue chip as "Uncheckpointed
  // Held" — internal data-model language, not a coordinator-facing status.
  // Matches the wording LeaveTimeline already uses for this state.
  UNCHECKPOINTED_HELD: 'On hold — being checked',
  // FU-21 hold reasons (lib/leave/leave-hold-reason.ts owns the full copy);
  // labelled here so the formatter never title-cases the keys.
  PAY_PROFILE_MISSING: 'Pay profile missing',
  PAY_PROFILE_AMBIGUOUS: 'More than one pay profile',
  leave_type_unregistered: 'Leave type not registered',
  // Leave registry keys and hold reasons for the two accrued-balance types
  // that stay registry-HELD until Medea confirms their accrual source
  // (docs/design/leave-enterprise-ux-2026-08-27/12-TOIL-ADO-ENABLEMENT.md).
  // Labels are ready so switch-on never renders "Toil" / "Ado".
  TOIL: 'Time off in lieu',
  ADO: 'Accrued day off',
  // NES ss106A-106E. Same wording as LEAVE_TYPE_DISPLAY_LABELS in
  // @statura/utils; without it the title-caser drops the "and".
  FAMILY_DOMESTIC_VIOLENCE: 'Family and domestic violence leave',
  TIME_IN_LIEU_TAKEN: 'Time in lieu taken',
  ACCRUED_DAY_OFF_TAKEN: 'Accrued day off taken',
  HELD_TOIL_EXACT_ACCRUAL_SOURCE_REQUIRED: 'Time off in lieu — waiting on the accrual source',
  HELD_ADO_EXACT_ACCRUAL_SOURCE_REQUIRED: 'Accrued day off — waiting on the accrual source',
  // People follow-up reminder delivery states (list_people_chases_controlled
  // and list_people_chase_timeline_controlled). Plain words for care staff.
  not_sent: 'Not sent yet',
  blocked_denied_destination: 'Not delivered (address blocked)',
  audited: 'Chase cycle reset',
};

/**
 * Format a SCREAMING_SNAKE_CASE or kebab-case enum value to Title Case.
 * Checks ENUM_DISPLAY_OVERRIDES first for acronyms and legislative terms,
 * then falls back to generic title-case conversion.
 * e.g., "CARE_QUALITY" -> "Care Quality", "AN_ACC" -> "AN-ACC"
 */
/**
 * Acronyms that should stay upper-cased when they appear as a word *inside* a
 * multi-word enum value (e.g. `SCHADS_ORDINARY` -> "SCHADS Ordinary"). Exact
 * whole-value matches are handled by ENUM_DISPLAY_OVERRIDES above; this handles
 * the embedded case after generic title-casing.
 */
const EMBEDDED_ACRONYMS: Record<string, string> = {
  Schads: 'SCHADS',
  Ahpra: 'AHPRA',
  Ndis: 'NDIS',
  Sirs: 'SIRS',
  Chsp: 'CHSP',
  Dva: 'DVA',
  Hcp: 'HCP',
  Sah: 'SAH',
  Ea: 'EA',
  Eba: 'EBA',
  Stp: 'STP',
  Myob: 'MYOB',
  Keypay: 'KeyPay',
  Pca: 'PCA',
  Rn: 'RN',
  En: 'EN',
  Ain: 'AIN',
  Pcw: 'PCW',
  Csv: 'CSV',
  Xlsx: 'XLSX',
  Inerva: 'Inerva',
  // Document category codes (DOC-53): EPOA, SDS and WHS documents, and
  // "..._ack" acknowledgement records.
  Epoa: 'EPOA',
  Sds: 'SDS',
  Whs: 'WHS',
  Ack: 'Acknowledgement',
};

// Derived from EMBEDDED_ACRONYMS keys so the map is the single source of truth.
const EMBEDDED_ACRONYM_RE = new RegExp(`\\b(${Object.keys(EMBEDDED_ACRONYMS).join('|')})\\b`, 'g');

/**
 * Leave hours resolution source (note 14, `resolve_leave_request_hours`):
 * the lowercase tokens `roster` / `pay_profile` / `roster_history` /
 * `custom_fields` / `default` (+ persisted `manual`) rendered through the
 * label map shared with the mobile app. Kept out of ENUM_DISPLAY_OVERRIDES so
 * an unrelated lowercase value such as "default" never picks up this copy.
 */
export function formatLeaveHoursSource(value: string | null | undefined): string {
  if (!value) return LEAVE_HOURS_SOURCE_LABELS.default;
  return (LEAVE_HOURS_SOURCE_LABELS as Record<string, string>)[value] ?? formatEnum(value);
}

export function formatEnum(value: string | null | undefined): string {
  if (!value) return '—';
  const override = ENUM_DISPLAY_OVERRIDES[value];
  if (override) return override;
  const titled = value
    .replace(/[_-]/g, ' ')
    .toLowerCase()
    .replace(/\b\w/g, (c) => c.toUpperCase());
  return titled.replace(EMBEDDED_ACRONYM_RE, (m) => EMBEDDED_ACRONYMS[m] ?? m);
}

/** Human-facing attended-payslip leave-proof outcomes. */
export function formatPayslipLeaveProofReason(value: string | null | undefined): string {
  const labels: Record<string, string> = {
    PAYSLIP_LEAVE_NOT_ON_DRAFT: 'Expected leave is not on the Xero draft payslip',
    PAYSLIP_LEAVE_UNEXPECTED: 'The Xero draft payslip has unexpected leave',
    PAYSLIP_LEAVE_TYPE_UNMAPPED: 'The leave type is not mapped to a Xero earnings rate',
    PAYSLIP_LEAVE_COVERING_SET_PARTIAL:
      'Leave requests paid together on this payslip are only partly covered',
    PAYSLIP_LEAVE_MANIFEST_DRIFT: 'Leave proof changed after verification',
  };
  return value ? (labels[value] ?? formatEnum(value)) : '—';
}

/**
 * Format a UUID to a short display reference.
 * Returns the first 8 chars if it looks like a UUID, otherwise returns the value as-is.
 */
export function formatId(value: string | null | undefined): string {
  if (!value) return '—';
  if (value.length === 36 && value.includes('-')) {
    return value.substring(0, 8).toUpperCase();
  }
  return value;
}

/**
 * Care-mode display labels (ADR-009 mode detection). Raw HOME_CARE/RESIDENTIAL/
 * MIXED enum values are banned from UI surfaces — always render through this.
 */
const MODE_LABELS: Record<string, string> = {
  HOME_CARE: 'Home care',
  RESIDENTIAL: 'Residential',
  MIXED: 'Home care + residential',
};

export function formatMode(mode: string | null | undefined): string {
  if (!mode) return '—';
  return MODE_LABELS[mode] ?? formatEnum(mode);
}

/** Shift handover codes (AM/PM/NIGHT) as the handover form labels them. */
const HANDOVER_SHIFT_LABELS: Record<string, string> = {
  AM: 'Morning',
  PM: 'Afternoon',
  NIGHT: 'Night',
};

export function formatHandoverShift(value: string | null | undefined): string {
  if (!value) return '—';
  return HANDOVER_SHIFT_LABELS[value.trim().toUpperCase()] ?? formatUiLabel(value);
}

/**
 * Format a date for user display, e.g. "Mon 18 May 2026".
 *
 * Accepts BOTH shapes and treats them differently, because they are different
 * things:
 *
 *   - A calendar date (`YYYY-MM-DD`) — a date of birth, an assignment date, a
 *     leave start. Anchored to noon UTC so it renders as the same calendar day in
 *     every timezone. The tenant timezone must NOT shift it.
 *   - An instant (`YYYY-MM-DDTHH:MM:SSZ`) — a shift start, a submitted_at, a
 *     due_at. The business date depends on the tenant timezone and must be derived
 *     WITH it.
 *
 * This previously regex-sliced the first ten characters for both, which silently
 * took the UTC calendar day off an instant and discarded the offset before any
 * timezone was applied. In Australia/Hobart (UTC+10/+11) every shift starting
 * before 10:00 local rendered as the PREVIOUS day: a 07:00 Mon 3 Aug shift is
 * stored 2026-08-02T21:00:00Z and displayed "Sun, 2 Aug".
 *
 * That reached a destructive confirmation — the "Remove worker from this shift?"
 * dialog — where a coordinator reads the date to check they have the right shift.
 *
 * Date-only inputs are unchanged, byte for byte.
 */
export function formatDateAU(
  isoDate: string | null | undefined,
  tz?: string,
  opts?: Intl.DateTimeFormatOptions,
): string {
  if (!isoDate) return '—';
  const base: Intl.DateTimeFormatOptions = {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    ...(tz ? { timeZone: tz } : {}),
    ...opts,
  };

  // An instant: keep the offset and let Intl resolve the business date in `tz`.
  // Detected by a time component, so a bare `YYYY-MM-DD` never takes this path.
  if (/^\d{4}-\d{2}-\d{2}[T ]\d{2}:\d{2}/.test(isoDate)) {
    const instant = new Date(isoDate);
    if (Number.isNaN(instant.getTime())) return isoDate;
    return instant.toLocaleDateString('en-AU', base);
  }

  // A calendar date: anchor at noon UTC so no timezone can shift the day.
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(isoDate);
  if (!m) return isoDate;
  const anchor = new Date(`${m[1]}-${m[2]}-${m[3]}T12:00:00Z`);
  return anchor.toLocaleDateString('en-AU', base);
}

/**
 * Format an ISO calendar date range, collapsing the year (and month, when shared),
 * e.g. "Mon 18 May – Sun 31 May 2026".
 */
export function formatDateRangeAU(
  startIso: string | null | undefined,
  endIso: string | null | undefined,
  tz?: string,
): string {
  if (!startIso || !endIso) return '—';
  const sameYear = startIso.slice(0, 4) === endIso.slice(0, 4);
  const start = formatDateAU(startIso, tz, sameYear ? { year: undefined } : undefined);
  const end = formatDateAU(endIso, tz);
  return `${start} – ${end}`;
}

/** Scan labels are contextual: PENDING also occurs in unrelated workflows. */
export function formatDocumentScanState(value: string | null | undefined): string {
  const labels: Record<string, string> = {
    PENDING: 'Waiting for virus check',
    CLEAN: 'Virus check passed',
    INFECTED: 'Blocked: failed virus check',
    SCAN_ERROR: 'Blocked: virus check could not finish',
    SKIPPED_LEGACY: 'Not checked (uploaded before checking started)',
  };
  return value && labels[value] ? labels[value]! : 'Virus check status unavailable';
}

// Presentation-only labels. Keep legacy formatEnum outputs stable for payroll,
// signed documents, exports, API responses and NOI-declared sources.
export const UI_LABEL_OVERRIDES: Record<string, string> = {
  MANUAL_HANDLING: 'Manual handling',
  care_plan_not_active_for_visit:
    'Care saved; billing held until the plan is verified for the visit date',
  // W3a contractor delivery holds (contractor-management.md §5.3).
  ACTUALS_MISSING: 'Held until visit hours are confirmed',
  CONTRACTOR_PRICING_UNRESOLVED: 'Held until the service price is confirmed',
  CONTRACTOR_WORKER_UNLINKED: 'Waiting for worker link',
  ABSENCE_CASE_PAY_REVIEW: 'Absence pay needs review',
  PAY_RATE_UNSUPPORTED_BY_AGREEMENT: 'Pay rate is not in the agreement',
  TIMESHEET_PAY_CONTEXT_NOT_RESOLVED: 'Timesheet pay calculation is incomplete',
  TIMESHEET_PAY_CONTEXT_STALE: 'Timesheet pay calculation needs refreshing',
  TENANT_ADMIN: 'Organisation administrator',
  SYSTEM_ADMIN: 'System administrator',
  REGISTERED_NURSE: 'Registered nurse',
  ENROLLED_NURSE: 'Enrolled nurse',
  FULL_TIME: 'Full time',
  PART_TIME: 'Part time',
  NOT_SPECIFIED: 'Not specified',
  RAC_PERMANENT: 'Permanent residential care',
  'staff.sensitive_fields_viewed': 'Sensitive fields viewed',
  'Staff.Sensitive Fields Viewed': 'Sensitive fields viewed',
  RATE_OPERATIONAL_ROUNDING_RECONCILIATION_REQUIRED:
    'Estimates await Services Australia rounding reconciliation',
  RESOLVED_OUTSIDE_STATURA: 'Resolved outside Statura',
  SERVICE_AREA_MISSING: 'Service area not set',
  // SAH E1 origin-bound delivery holds (S1 20991029700005).
  MANUAL_FINANCE_REVIEW_REQUIRED:
    "Finance review needed: this visit's attendance was recorded and billing is held until the brokered-visit finance review is available",
  SERIES_FINANCE_REVIEW_REQUIRED:
    "Finance review needed: this repeating visit's attendance was recorded and billing is held until the brokered-visit finance review is available",
  DEGRADED: 'Status unknown',
  UNDETERMINED: 'Status unknown',
  AGED_CARE_SCREENING_UNDETERMINED: 'Aged care screening status unknown',
  INACTIVE: 'Inactive',
  before_first_shift: 'Before first shift',
  within_30_days: 'Within 30 days',
  collect_by: 'Collect by date',
  after_start: 'After start',
  current_workforce: 'Current workforce',
  former_worker: 'Former worker',
  LOG_AFTER: 'Record work after delivery',
  ROSTERED: 'Rostered delivery',
  RAC: 'Residential aged care (RAC)',
  TCP: 'Transition care (TCP)',
  STRC: 'Short-term restorative care',
  NOT_CURRENT: 'Not current',
  HR_OFFICER: 'HR officer',
  HR_DIRECTOR: 'HR director',
  CLINICAL_SCOPE_MINIMUM: 'Minimum clinical scope',
  ENFORCE_ROSTER: 'Enforce roster',
  ENFORCE_ALL: 'Enforce roster and downstream controls',
  ENFORCE_CONTEXT: 'Enforce pay context',
  ENFORCE_EXPORT: 'Enforce pay export',
  UNSET_ENFORCE_DEFAULT: 'Legacy unset (effective Enforce)',
  BOOTSTRAP_EFFECTIVE_V1: 'Inherited compatibility settings',
  NEW_TENANT_DEFAULT_V2: 'New provider defaults',
  CONTROLLED_PATCH_V2: 'Explicit audited provider choice',
  RAC_RESIDENT_FEES: 'Residential aged care fees',
  CONTRIBUTION_CAPS: 'Contribution caps',
  MEANS_TEST: 'Means test',
  RESIDENTIAL_CHARGE: 'Residential charge',
  FEE_ADVICE_CHANGED: 'Fee advice changed after billing',

  PRODA_DEVICE: 'PRODA device activation',
  PRODA_KEY: 'PRODA signing key',
  NASH_CERTIFICATE: 'NASH certificate',
  B2G_SUBSCRIPTION: 'B2G subscription credential',
  B2G_REGISTRATION: 'B2G machine credential',
  OUTCOME_UNKNOWN: 'Outcome needs reconciliation',
  CHECK_FAILED: 'Check failed',
  DISALLOW: 'No negative balance',
  ALLOW_WITH_REASON: 'Negative balance requires a reason',
  ALLOW_WITH_REASON_AND_CHECKER: 'Negative balance requires a reason and second approver',
  // Approved-timesheet correction kinds and the approval-withdrawal events.
  // The propose form on Payroll > Corrections names these in plain language;
  // the history table, badges and evidence views must say the same words
  // (design authority, 8 Sep 2026, UX-13) instead of the title-case fallback
  // ("Source Restatement", "Reopened For Correction").
  SOURCE_RESTATEMENT: 'Corrected facts',
  VOID_DUPLICATE: 'Void duplicate',
  REPLACE_WITH_LEAVE: 'Replaced with leave',
  TIME_BREAK: 'Time or break correction',
  REOPENED_FOR_CORRECTION: 'Reopened for correction',
  TIMESHEET_APPROVAL_REOPENED: 'Approval withdrawn',
  // CHSP reporting and My Aged Care identity reconciliation.
  CHSP_REPORTING: 'CHSP reporting',
  CHSP_MAC_COMPLETENESS: 'CHSP My Aged Care ID completeness',
  DEX_MONTHLY: 'CHSP monthly DEX report',
  FINANCIAL_DECLARATION: 'CHSP annual financial declaration',
  CHILD_SAFETY: 'CHSP child safety compliance statement',
  MISSING_MAC_ID: 'My Aged Care ID missing',
  CONFLICTING_MAC_ID: 'My Aged Care IDs do not match',
  ADOPT_CLIENT_MAC: 'Use the client record My Aged Care ID in DEX',
  CORRECT_CLIENT_MAC: 'Correct the client record My Aged Care ID',
  SSD_ONLY: 'Sector Support and Development only',
  // Acronyms
  CHSP: 'CHSP',
  NDIS: 'NDIS',
  DVA: 'DVA',
  AHPRA: 'AHPRA',
  AN_ACC: 'AN-ACC',
  'AN ACC': 'AN-ACC',
  MAC_ID: 'My Aged Care ID',
  SAH: 'SAH',
  EA: 'EA',
  EBA: 'EBA',
  STP: 'STP',
  MYOB: 'MYOB',
  KEYPAY: 'KeyPay',
  PCA: 'PCA',
  RN: 'RN',
  EN: 'EN',
  HCA1: 'HCA1',
  HCA2: 'HCA2',
  HCA3: 'HCA3',
  HCA4: 'HCA4',
  HCA5: 'HCA5',
  HCA6: 'HCA6',
  // Display labels use sentence case; stored tokens retain their canonical spelling.
  AWAITING_CLOCK: 'No clock yet',
  EVERYDAY_LIVING: 'Everyday living',
  INDEPENDENCE: 'Independence',
  CLINICAL_CARE: 'Clinical care',
  CARE_WORKER: 'Care worker',
  PERSONAL_CARE: 'Personal care',
  MEAL_PREPARATION: 'Meal preparation',
  DOMESTIC_ASSISTANCE: 'Domestic assistance',
  SOCIAL_SUPPORT: 'Social support',
  CSV: 'CSV',
  XLSX: 'XLSX',
  INERVA: 'Inerva',
  SUPPORT_AT_HOME: 'Support at Home',
  NONE_SOURCE_ONLY: 'None (source only)',
  VACANCY: 'Vacancy',
  HOLD: 'Hold',
  AGENCY: 'Agency worker',
  AGENCY_UNFILLED: 'Agency unfilled',
  STAFFED: 'Staffed',
  NOT_A_SHIFT: 'Not a shift',
  ASSUMED_REPEAT: 'Assumed repeat',
  CONFIRMATION_PENDING: 'Confirmation pending',
  OVERLAP: 'Overlap',
  UNMATCHED_NAME: 'Unmatched name',
  // Without these the generic title-caser renders them as "Whs", "Ain", "Sirs"
  // and so on wherever the token reaches a label or a dropdown option.
  WHS: 'WHS',
  AIN: 'AIN',
  SIRS: 'SIRS',
  ACQSC: 'ACQSC',
  LMS: 'LMS',
  TFN: 'TFN',
  ABN: 'ABN',
  BPAY: 'BPAY',
  ELMO: 'ELMO',
  GST: 'GST',
  // Screening types
  AHPRA_REGISTRATION: 'AHPRA registration',
  NATIONAL_POLICE_CHECK: 'National police check',
  NDIS_WORKER_SCREENING: 'NDIS worker screening',
  WORKING_WITH_CHILDREN: 'Working with children',
  AGED_CARE_WORKER_SCREENING: 'Aged care worker screening',
  INTERNATIONAL_POLICE_CHECK: 'International police check',
  FIRST_AID: 'First aid',
  CPR: 'CPR',
  // Risk levels
  NOT_ASSESSED: 'Not assessed',
  not_assessed: 'Not assessed',
  // SIRS incident types — current categories reflect Aged Care Act 2024 s 16.
  UNREASONABLE_USE_OF_FORCE: 'Unreasonable use of force',
  UNLAWFUL_SEXUAL_CONTACT: 'Unlawful sexual contact',
  PSYCHOLOGICAL_EMOTIONAL_ABUSE: 'Psychological or emotional abuse',
  UNEXPECTED_DEATH: 'Unexpected death',
  STEALING_FINANCIAL_COERCION: 'Stealing or financial coercion',
  NEGLECT: 'Neglect',
  INAPPROPRIATE_RESTRICTIVE_PRACTICES: 'Inappropriate restrictive practices',
  UNEXPLAINED_ABSENCE: 'Unexplained absence',
  SERIOUS_HARM: 'Historical serious harm category',
  OTHER_SERIOUS: 'Historical other serious category',
  // QI domains
  physical_restraint: 'Restrictive practices',
  PHYSICAL_RESTRAINT: 'Restrictive practices',
  medication_management: 'Medication management',
  MEDICATION_MANAGEMENT: 'Medication management',
  // Funding programs — defensive acronym preservation
  HCP: 'HCP',
  hcp: 'HCP',
  // Leave settlement axis (lib/leave/leave-settlement-axis.ts). Tokens whose
  // generic title-casing would misrender a product/provider name or read as
  // jargon on a worker-facing chip.
  STATURA_PAID: 'Paid by Statura',
  SENT_TO_XERO: 'Sent to Xero',
  SETTLED_EXTERNAL: 'Settled externally',
  PUSH_INDETERMINATE: 'Push outcome unknown',
  RECONCILE_MISMATCH: 'Reconcile mismatch',
  VOID_PENDING: 'Payroll removal pending',
  VOID_FAILED: 'Payroll removal failed',
  REVERSAL_REQUIRED: 'Reversal required',
  REVERSAL_RECONCILED: 'Reversal reconciled',
  REVERSAL_FAILED: 'Reversal failed',
  // Leave push faults (lib/leave/leave-push-core.ts, WP-3 D1). Operator
  // language for the leave-payments row detail and exception card — the raw
  // token would read as data-model jargon.
  LEAVE_WORKER_PAY_HELD: 'All worker pay held for leave correction',
  LEAVE_PRICING_WORKER_HOLD: 'All worker pay held for leave correction',
  MAPPING_UNRESOLVED: 'Xero mapping missing or ambiguous',
  PAY_PERIODS_UNRESOLVED: 'Xero pay calendar unavailable',
  PAYLOAD_INVALID: 'Leave days do not build a valid Xero request',
  NO_SEGMENTS: 'No leave days recorded',
  TOTAL_HOURS_UNAVAILABLE: 'Request hours unavailable',
  REQUEST_NOT_APPROVED: 'Not approved',
  ALREADY_PUSHED: 'Already sent to Xero',
  SYNC_STATE_NOT_PUSHABLE: 'Not in a pushable state',
  INDETERMINATE_UNRESOLVED: 'Push outcome unknown — resolve before retrying',
  PROVIDER_REJECTED: 'Xero refused the request',
  PROVIDER_RATE_LIMITED: 'Xero is busy — try again shortly',
  MARKER_STAMP_FAILED: 'Sent to Xero but not recorded — resolve to adopt it',
  STATE_WRITE_FAILED: 'Could not record the outcome — try again',
  SELF_PUSH_FORBIDDEN: 'Your own request — another operator must send it',
  SHARED_ACCOUNT_FORBIDDEN: 'Shared account — sign in as yourself to move money',
  PUSH_IN_FLIGHT: 'Already being sent — wait for the outcome',
  NOT_INDETERMINATE: 'Nothing to resolve for this request',
  LEAVE_PUSH_LOADING_GATES_UNRESOLVED: 'Annual leave loading not ready to send',
  LEAVE_PUSH_LOADING_DISPATCH_WITHHELD: 'Annual leave loading delivery not switched on',
  // Attended Xero priced-payslip refusals. These can reach the journal and
  // must not degrade to implementation tokens in an operator-facing panel.
  PAYSLIP_CENTS_UNREPRESENTABLE: 'Approved pay cannot be represented at Xero wire precision',
  PAYSLIP_WRITE_TARGET_RATE_HAS_FOREIGN_LINE:
    'Targeted Xero earnings rate has a manual or non-template payment',
  RESOLVER_PROVIDER_UNAVAILABLE: 'Could not check Xero — try again',
  RESOLVER_SCOPE_INSUFFICIENT: 'Xero check could not cover these dates — needs manual review',
  RESOLVER_AMBIGUOUS: 'Conflicting records in Xero — needs manual review',
  RETRY_EXISTING_APPLICATION_FOUND:
    'This leave is already in Xero. Link it instead of sending again: open Leave payments on Roster > Time & closeout > Timesheets and choose Resolve now.',
  // WP-6 [CU3] push-time balance verdict recheck faults.
  BALANCE_INSUFFICIENT_AT_PUSH: 'Balance no longer covers this leave — cannot send',
  XERO_APPLICATION_ALREADY_PRESENT:
    'Xero already has leave of this type on these dates — check it was not keyed by hand before sending',
  PERIOD_ALREADY_POSTED: 'This pay period is already posted in Xero — not sent again',
  PERIOD_STATUS_UNVERIFIED:
    'Pay period status not confirmed from Xero yet — will retry after the next sync',
  ORIGINAL_IN_PROCESSED_PERIOD:
    'The earlier leave is in a processed Xero period — payroll must confirm the correction before this change is sent',
  BALANCE_CONTESTED_AT_PUSH:
    'Balance covers this leave, but pending leave requests contest it — decide the pending request or override',
  BALANCE_STALE_AT_PUSH: 'Balance too old to verify — refresh, or send with an override',
  BALANCE_UNVERIFIABLE_AT_PUSH: 'Balance could not be verified — refresh, or send with an override',
  BALANCE_AUTHORISATION_FAILED:
    'Your role cannot read the balance basis — authorisation parity fix required, not an override',
  BALANCE_OVERRIDE_REASON_REQUIRED: 'Override reason too short — at least 10 characters',
  // Leave decision axis (lib/leave/leave-operational-projection.ts). Without
  // this the generic title-caser renders the queue chip as "Uncheckpointed
  // Held" — internal data-model language, not a coordinator-facing status.
  // Matches the wording LeaveTimeline already uses for this state.
  UNCHECKPOINTED_HELD: 'On hold — being checked',
  // FU-21 hold reasons (lib/leave/leave-hold-reason.ts owns the full copy);
  // labelled here so the formatter never title-cases the keys.
  PAY_PROFILE_MISSING: 'Pay profile missing',
  PAY_PROFILE_AMBIGUOUS: 'More than one pay profile',
  leave_type_unregistered: 'Leave type not registered',
  // Leave registry keys and hold reasons for the two accrued-balance types
  // that stay registry-HELD until Medea confirms their accrual source
  // (docs/design/leave-enterprise-ux-2026-08-27/12-TOIL-ADO-ENABLEMENT.md).
  // Labels are ready so switch-on never renders "Toil" / "Ado".
  TOIL: 'Time off in lieu',
  ADO: 'Accrued day off',
  // NES ss106A-106E. Same wording as LEAVE_TYPE_DISPLAY_LABELS in
  // @statura/utils; without it the title-caser drops the "and".
  FAMILY_DOMESTIC_VIOLENCE: 'Family and domestic violence leave',
  TIME_IN_LIEU_TAKEN: 'Time in lieu taken',
  ACCRUED_DAY_OFF_TAKEN: 'Accrued day off taken',
  HELD_TOIL_EXACT_ACCRUAL_SOURCE_REQUIRED: 'Time off in lieu — waiting on the accrual source',
  HELD_ADO_EXACT_ACCRUAL_SOURCE_REQUIRED: 'Accrued day off — waiting on the accrual source',
  // People follow-up reminder delivery states (list_people_chases_controlled
  // and list_people_chase_timeline_controlled). Plain words for care staff.
  not_sent: 'Not sent yet',
  blocked_denied_destination: 'Not delivered (address blocked)',
  audited: 'Chase cycle reset',
};

const UI_ACRONYMS: Record<string, string> = {
  'an-acc': 'AN-ACC',
  sirs: 'SIRS',
  ndis: 'NDIS',
  rn: 'RN',
  en: 'EN',
  gp: 'GP',
  mac: 'MAC',
  whs: 'WHS',
  ipc: 'IPC',
  s8: 'S8',
  sah: 'SAH',
  rac: 'RAC',
  chsp: 'CHSP',
  hcp: 'HCP',
  au: 'AU',
  id: 'ID',
  pdf: 'PDF',
  csv: 'CSV',
  pin: 'PIN',
  mfa: 'MFA',
  hr: 'HR',
  ceo: 'CEO',
  rad: 'RAD',
  dap: 'DAP',
  schads: 'SCHADS',
  ahpra: 'AHPRA',
  dva: 'DVA',
  ea: 'EA',
  eba: 'EBA',
  stp: 'STP',
  myob: 'MYOB',
  pca: 'PCA',
  ain: 'AIN',
  pcw: 'PCW',
  xlsx: 'XLSX',
  epoa: 'EPOA',
  sds: 'SDS',
  cpr: 'CPR',
  gpms: 'GPMS',
  news2: 'NEWS2',
  gst: 'GST',
  lgbtqia: 'LGBTQIA',
  xero: 'Xero',
  keypay: 'KeyPay',
  inerva: 'Inerva',
  ack: 'acknowledgement',
};

/** Sentence-case on-screen copy; never use for persisted or exported values. */
export function formatUiLabel(value: string | null | undefined, fallback = '—'): string {
  if (typeof value !== 'string' || !value.trim()) return fallback;
  const trimmed = value.trim();
  const override = UI_LABEL_OVERRIDES[trimmed] ?? UI_LABEL_OVERRIDES[trimmed.toUpperCase()];
  if (override) return override;
  const sentence = trimmed
    .replace(/(^|[_.\s-])AN[ _-]ACC(?=$|[_.\s-])/gi, '$1AN-ACC')
    .replace(/AN-ACC|[_.-]+/g, (part) => (part === 'AN-ACC' ? part : ' '))
    .trim()
    .toLowerCase()
    .replace(/^\w/, (char) => char.toUpperCase());
  return sentence
    .replace(/AN-ACC|\b[\w]+\b/gi, (word) => UI_ACRONYMS[word.toLowerCase()] ?? word)
    .replace(/^\w/, (char) => char.toUpperCase());
}

/** Human role names; the type requires every canonical role to be covered. */
export const USER_ROLE_LABELS = {
  SYSTEM_ADMIN: 'System administrator',
  TENANT_ADMIN: 'Organisation administrator',
  CEO: 'Chief executive',
  OWNER: 'Owner',
  OPERATIONS_DIRECTOR: 'Operations director',
  CLINICAL_DIRECTOR: 'Clinical director',
  FINANCE_DIRECTOR: 'Finance director',
  QUALITY_DIRECTOR: 'Quality director',
  HR_DIRECTOR: 'HR director',
  REGION_MANAGER: 'Region manager',
  REGION_COMPLIANCE_OFFICER: 'Region compliance officer',
  REGION_FINANCE_OFFICER: 'Region finance officer',
  COORDINATOR: 'Coordinator',
  TEAM_LEADER: 'Team leader',
  REGISTERED_NURSE: 'Registered nurse',
  ENROLLED_NURSE: 'Enrolled nurse',
  CARE_WORKER: 'Care worker',
  ALLIED_HEALTH: 'Allied health',
  FINANCE_OFFICER: 'Finance officer',
  PAYROLL_OFFICER: 'Payroll officer',
  CLIENT: 'Client',
  FAMILY_CARER: 'Family/carer',
  CLINICAL_LEAD: 'Clinical lead',
  AGENCY_CONTRACTOR: 'Agency contractor',
  FACILITY_MANAGER: 'Facility manager',
  QUALITY_OFFICER: 'Quality officer',
  COMPLIANCE_OFFICER: 'Compliance officer',
  CASE_MANAGER: 'Case manager',
  LIFESTYLE_OFFICER: 'Lifestyle officer',
  HR_OFFICER: 'HR officer',
} satisfies Record<UserRole, string>;

const LEGACY_ROLE_LABELS: Record<string, string> = {
  ADMIN: 'Administrator',
  NURSE: 'Clinical staff',
  PERSONAL_CARE_WORKER: 'Care worker',
  ASSISTANT_IN_NURSING: 'Care worker',
  'CARE-WORKER': 'Care worker',
  FINANCE: 'Finance',
  READONLY: 'Read only',
};

export function formatUserRole(value: string | null | undefined): string {
  const role = value?.trim().toUpperCase() ?? '';
  return (
    (USER_ROLE_LABELS as Record<string, string>)[role] ??
    LEGACY_ROLE_LABELS[role] ??
    formatUiLabel(value)
  );
}
